import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Shield, FileText, Cookie, Scale, RotateCcw, Mail } from 'lucide-react';
import { usePageT } from '../i18n/usePageT';
import { SEO } from '../components/SEO';
import LEGAL from '../i18n/legalContent.json';

/**
 * Legal pages for Respectful Markets — content lives in /i18n/legalContent.json (French source)
 * and is translated into the 15 site languages through the usePageT() bank.
 *
 * NOTE: Generic templates aligned with French/EU regulation
 * (LCEN 2004-575, RGPD/GDPR, Code de la consommation). Replace placeholder
 * company information ([…]) with the operator's real legal data before going live.
 */

const COMPANY = {
  name: 'Respectful Markets SAS',
  legalForm: 'Société par Actions Simplifiée',
  capital: '10 000 € (à valider lors de l\'immatriculation)',
  rcs: 'RCS — en cours d\'immatriculation',
  siret: 'En cours d\'attribution',
  vat: 'En cours d\'attribution',
  address: 'France (adresse du siège à finaliser)',
  director: 'Direction de la publication — à désigner',
  email: 'contact@respectfulmarkets.com',
  supportEmail: 'support@respectfulmarkets.com',
  dpoEmail: 'dpo@respectfulmarkets.com',
  phone: '+33 1 23 45 67 89',
  hostName: 'Emergent Cloud Hosting',
  hostAddress: 'Centre de données européen, Union Européenne',
  updatedAt: '1er février 2026',
  inRegistration: true,
};

const substitute = (text) => text.replace(/\{(\w+)\}/g, (m, k) => (COMPANY[k] !== undefined ? COMPANY[k] : m));

// Renders **bold** and [label](href) markers inside a translated string
const parseRich = (s) => {
  const out = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<strong key={i++}>{m[1]}</strong>);
    } else if (m[3].startsWith('/')) {
      out.push(<Link key={i++} to={m[3]}>{m[2]}</Link>);
    } else {
      out.push(<a key={i++} href={m[3]} target={m[3].startsWith('mailto:') ? undefined : '_blank'} rel="noreferrer noopener">{m[2]}</a>);
    }
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
};

const Rich = ({ text, L }) => {
  const s = substitute(L(text));
  const lines = s.split('\n');
  return lines.map((line, idx) => (
    <React.Fragment key={idx}>
      {parseRich(line)}
      {idx < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

const Block = ({ b, L }) => {
  switch (b.t) {
    case 'h2': return <h2><Rich text={b.x} L={L} /></h2>;
    case 'h3': return <h3><Rich text={b.x} L={L} /></h3>;
    case 'p': return <p><Rich text={b.x} L={L} /></p>;
    case 'callout': return <div className="callout"><p style={{ marginBottom: 0 }}><Rich text={b.x} L={L} /></p></div>;
    case 'ul':
      return (
        <ul>
          {b.x.map((li, i) => <li key={i}><Rich text={li} L={L} /></li>)}
        </ul>
      );
    case 'table':
      return (
        <table>
          <thead>
            <tr>{b.head.map((h, i) => <th key={i}><Rich text={h} L={L} /></th>)}</tr>
          </thead>
          <tbody>
            {b.rows.map((row, ri) => (
              <tr key={ri}>{row.map((c, ci) => <td key={ci}><Rich text={c} L={L} /></td>)}</tr>
            ))}
          </tbody>
        </table>
      );
    default: return null;
  }
};

const LegalLayout = ({ icon: Icon, docKey, testId }) => {
  const L = usePageT();
  const doc = LEGAL[docKey];
  const S = LEGAL.shared;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#FBF8F3] min-h-screen" data-testid={testId}>
      <SEO title={L(doc.title)} description={L(doc.subtitle)} url={typeof window !== 'undefined' ? window.location.origin + window.location.pathname : undefined} />
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F4C5C] via-[#0F4C5C] to-[#0B3A47] text-white">
        <div className="container-main py-16 md:py-20">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-6">
            <Link to="/" className="hover:text-[#E9C46A] transition-colors">{L(S.home, 'Home')}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white/90">{L(S.legalInfo)}</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#E9C46A]">{L(doc.title)}</span>
          </nav>
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-2xl bg-[#E9C46A]/15 border border-[#E9C46A]/30 flex items-center justify-center flex-shrink-0">
              <Icon className="w-7 h-7 text-[#E9C46A]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight">{L(doc.title)}</h1>
              <p className="mt-3 text-base md:text-lg text-white/80 max-w-2xl">{L(doc.subtitle)}</p>
              <p className="mt-4 text-xs uppercase tracking-[0.3em] text-[#E9C46A]/80">
                {L(S.updated)} {COMPANY.updatedAt}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container-main py-12 md:py-16">
        {COMPANY.inRegistration && (
          <div className="mb-8 rounded-2xl border-2 border-[#E9C46A]/40 bg-[#FFF8E6] p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#E9C46A]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[#0F4C5C] font-bold text-lg">!</span>
            </div>
            <div className="text-sm text-[#0F4C5C]">
              <p className="font-semibold mb-1">{L(S.regBannerTitle)}</p>
              <p className="text-[#0F4C5C]/80">{L(S.regBannerBody)}</p>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
          {/* Side nav */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 bg-white rounded-2xl border border-[#0F4C5C]/10 p-5 shadow-sm">
              <p className="text-xs uppercase tracking-widest text-[#0F4C5C]/60 font-semibold mb-4">{L(S.docs)}</p>
              <nav className="flex flex-col gap-1 text-sm">
                <SideLink to="/legal" icon={FileText}>{L(S.navLegal)}</SideLink>
                <SideLink to="/terms" icon={Scale}>{L(S.navTerms)}</SideLink>
                <SideLink to="/privacy" icon={Shield}>{L(S.navPrivacy)}</SideLink>
                <SideLink to="/cookies" icon={Cookie}>{L(S.navCookies)}</SideLink>
                <SideLink to="/returns" icon={RotateCcw}>{L(S.navReturns)}</SideLink>
              </nav>
              <div className="mt-6 pt-5 border-t border-[#0F4C5C]/10">
                <p className="text-xs text-[#0F4C5C]/70 mb-2">{L(S.legalQuestion)}</p>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#0F4C5C] hover:text-[#E9C46A] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {COMPANY.email}
                </a>
              </div>
            </div>
          </aside>

          {/* Content */}
          <article className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 md:p-12 shadow-sm legal-prose">
            {doc.blocks.map((b, i) => <Block key={i} b={b} L={L} />)}
          </article>
        </div>
      </div>

      {/* Inline styles for legal-prose */}
      <style>{`
        .legal-prose h2 { font-size: 1.5rem; font-weight: 700; color: #0F4C5C; margin-top: 2.5rem; margin-bottom: 1rem; letter-spacing: -0.01em; }
        .legal-prose h2:first-child { margin-top: 0; }
        .legal-prose h3 { font-size: 1.125rem; font-weight: 600; color: #0F4C5C; margin-top: 1.75rem; margin-bottom: 0.75rem; }
        .legal-prose p { color: #374151; line-height: 1.75; margin-bottom: 1rem; font-size: 0.975rem; }
        .legal-prose ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 1rem; color: #374151; }
        .legal-prose ul li { margin-bottom: 0.5rem; line-height: 1.7; }
        .legal-prose strong { color: #0F4C5C; font-weight: 600; }
        .legal-prose a { color: #0F4C5C; text-decoration: underline; text-decoration-color: #E9C46A; text-underline-offset: 3px; }
        .legal-prose a:hover { color: #E9C46A; }
        .legal-prose .callout { background: #FBF8F3; border-left: 3px solid #E9C46A; padding: 1rem 1.25rem; border-radius: 0.5rem; margin: 1.25rem 0; font-size: 0.925rem; color: #0F4C5C; }
        .legal-prose hr { border: 0; border-top: 1px solid rgba(15,76,92,0.1); margin: 2.5rem 0; }
        .legal-prose table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.9rem; }
        .legal-prose th, .legal-prose td { border: 1px solid rgba(15,76,92,0.15); padding: 0.6rem 0.85rem; text-align: left; }
        .legal-prose th { background: #FBF8F3; color: #0F4C5C; font-weight: 600; }
      `}</style>
    </div>
  );
};

const SideLink = ({ to, icon: Icon, children }) => {
  const isActive = typeof window !== 'undefined' && window.location.pathname === to;
  return (
    <Link
      to={to}
      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors ${
        isActive
          ? 'bg-[#0F4C5C] text-white'
          : 'text-[#0F4C5C]/80 hover:bg-[#0F4C5C]/5 hover:text-[#0F4C5C]'
      }`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span>{children}</span>
    </Link>
  );
};

export const LegalNoticePage = () => <LegalLayout icon={FileText} docKey="legal" testId="legal-notice-page" />;
export const TermsPage = () => <LegalLayout icon={Scale} docKey="terms" testId="terms-page" />;
export const PrivacyPage = () => <LegalLayout icon={Shield} docKey="privacy" testId="privacy-page" />;
export const CookiesPage = () => <LegalLayout icon={Cookie} docKey="cookies" testId="cookies-page" />;
export const ReturnsPage = () => <LegalLayout icon={RotateCcw} docKey="returns" testId="returns-page" />;
